// Pages/Support/WithdrawRequests.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Stack,
  InputAdornment,
  MenuItem,
  Divider,
  CircularProgress,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllWithdrawals,
  approveWithdrawal,
  rejectWithdrawal,
  completeWithdrawal,
} from "../../store/Slices/withdrawSlice";
// (اختياري) إن كان لديك تهيئة سوكِت للدعم:
// import { initSupportSocket, disposeSockets } from "../../lib/socket";

const fmt = (n, d = 8) => Number(n ?? 0).toFixed(d);
const dt = (v) => new Date(v).toLocaleString();

const StatusChip = ({ status }) => {
  const s = String(status || "").toLowerCase();
  const map = {
    pending: { color: "warning", label: "Pending" },
    approved: { color: "info", label: "Approved" },
    completed: { color: "success", label: "Completed" },
    rejected: { color: "error", label: "Rejected" },
  };
  const conf = map[s] || { color: "default", label: status || "—" };
  return (
    <Chip
      size="small"
      color={conf.color}
      label={conf.label}
      variant="outlined"
    />
  );
};

export default function WithdrawRequests() {
  const dispatch = useDispatch();
  // لاحظ: الشريحة الجديدة تستخدم مفاتيح all / listLoading / actionLoading
  const { all = [], listLoading, actionLoading } = useSelector(
    (s) => s.withdraw ?? {}
  );
  const loading = !!(listLoading || actionLoading);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [reason, setReason] = useState("");

  // تحسينات UX
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("All"); // All | Pending | Approved | Rejected | Completed

  useEffect(() => {
    dispatch(fetchAllWithdrawals());
    // (اختياري) تفعيل السوكِت لتحديث حي:
    // initSupportSocket(dispatch, { joinExplicit: true });
    // return () => { disposeSockets(); };
  }, [dispatch]);

  const copy = async (v) => {
    try {
      await navigator.clipboard.writeText(v || "");
    } catch {}
  };

  const onApprove = async (id) => {
    await dispatch(approveWithdrawal(id));
    dispatch(fetchAllWithdrawals());
  };

  const onReject = (id) => {
    setCurrentId(id);
    setReason("");
    setRejectOpen(true);
  };

  const confirmReject = async () => {
    await dispatch(rejectWithdrawal({ id: currentId, reason }));
    setRejectOpen(false);
    setReason("");
    dispatch(fetchAllWithdrawals());
  };

  const onComplete = async (id) => {
    // لا يوجد TXID — تأكيد مباشرة
    await dispatch(completeWithdrawal({ id }));
    dispatch(fetchAllWithdrawals());
  };

  // فلترة + بحث
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all
      .filter((r) =>
        statusFilter === "All" ? true : r.status === statusFilter
      )
      .filter((r) => {
        if (!needle) return true;
        const hay = [
          r.asset_symbol,
          r.network,
          r.to_address,
          r?.user_id?.username,
          r?.user_id?.email,
          r?._id,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(needle);
      });
  }, [all, q, statusFilter]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Toolbar */}
      <Card
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          borderRadius: 3,
          mb: 2,
          border: "1px solid",
          borderColor: "divider",
          backdropFilter: "blur(6px)",
        }}
      >
        <CardContent sx={{ py: 1.5 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Typography variant="h6">Withdrawal Requests (Support)</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                placeholder="Search user / email / address / id"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: { xs: "100%", md: 340 } }}
              />
              <TextField
                size="small"
                select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ minWidth: 160 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterListIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              >
                {["All", "Pending", "Approved", "Rejected", "Completed"].map(
                  (s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  )
                )}
              </TextField>
              <Tooltip title="Refresh">
                <span>
                  <IconButton
                    onClick={() => dispatch(fetchAllWithdrawals())}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={18} /> : <RefreshIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* List */}
      <Card
        sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}
      >
        <CardContent>
          {filtered.length === 0 ? (
            <Typography color="text.secondary">No requests found.</Typography>
          ) : (
            <Box sx={{ display: "grid", gap: 12 }}>
              {filtered.map((it) => (
                <Card
                  key={it._id}
                  variant="outlined"
                  sx={{ borderRadius: 2, borderColor: "divider" }}
                >
                  <CardContent sx={{ pt: 1.5 }}>
                    {/* Header line */}
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      spacing={1}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2">
                          {it.asset_symbol} • {it.network}
                        </Typography>
                        <StatusChip status={it.status} />
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        {it.status === "Pending" && (
                          <>
                            <Button
                              variant="contained"
                              color="success"
                              onClick={() => onApprove(it._id)}
                            >
                              Approve
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => onReject(it._id)}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        {it.status === "Approved" && (
                          <Button
                            variant="contained"
                            onClick={() => onComplete(it._id)}
                          >
                            Mark as Completed
                          </Button>
                        )}
                      </Stack>
                    </Stack>

                    <Divider sx={{ my: 1.25 }} />

                    {/* Body */}
                    <Stack spacing={0.8}>
                      <Typography variant="body2">
                        User: <b>{it.user_id?.username || "—"}</b> (
                        {it.user_id?.email || "—"})
                      </Typography>

                      <Typography variant="body2">
                        Amount:{" "}
                        <b>
                          {fmt(it.amount)} {it.asset_symbol}
                        </b>
                        {" • "}Price: <b>{fmt(it.price_usdt ?? 0, 4)} USDT</b>
                      </Typography>

                      {/* الرسوم القياسية الجديدة */}
                      <Typography variant="body2">
                        Platform fee:{" "}
                        {it.asset_symbol === "USDT"
                          ? `5.00000000 ${it.asset_symbol}`
                          : `${fmt(it.platform_fee_asset)} ${
                              it.asset_symbol
                            } (≈ ${fmt(it.platform_fee_usdt, 2)} USDT)`}
                      </Typography>
                      <Typography variant="body2">
                        Network fee:{" "}
                        {it.asset_symbol === "USDT"
                          ? `0.00000000 ${it.asset_symbol}`
                          : `${fmt(it.network_fee_asset)} ${
                              it.asset_symbol
                            } (${((it.network_fee_pct || 0) * 100).toFixed(
                              2
                            )}%)`}
                      </Typography>
                      <Typography variant="body2">
                        Total fees:{" "}
                        <b>
                          {fmt(it.total_fee_asset)} {it.asset_symbol}
                        </b>{" "}
                        • Net:{" "}
                        <b>
                          {fmt(it.net_amount)} {it.asset_symbol}
                        </b>
                      </Typography>

                      <Typography variant="body2">
                        To: {it.to_address}{" "}
                        <Tooltip title="Copy address">
                          <IconButton
                            size="small"
                            onClick={() => copy(it.to_address)}
                          >
                            <ContentCopyIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Typography>

                      <Typography variant="caption" color="text.secondary">
                        Created: {dt(it.created_at)}
                        {it.updated_at
                          ? ` • Updated: ${dt(it.updated_at)}`
                          : ""}
                      </Typography>

                      {it.reject_reason && (
                        <Typography variant="caption" color="error">
                          Reason: {it.reject_reason}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reject Withdrawal</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmReject}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
